import {useCallback, useEffect, useRef, useState} from "react";
import * as rawSpeciesImages from '../assets/data/species-images-merged.json';
import {toRootRelativeImagePath} from "./imagePaths";

const THUMB_SIZE = 300;
const MEDIUM_SIZE = 750;

const baseSpeciesImages = Object.fromEntries(Object.entries(rawSpeciesImages).map(([taxonomicOrder, image]: [string, any]) => {
    return [taxonomicOrder, {
        ...image,
        thumb: toRootRelativeImagePath(image.thumb),
        medium: toRootRelativeImagePath(image.medium)
    }];
}));

export const useGetSpeciesImages = () => {
    const [speciesImages, setSpeciesImages] = useState<any>(baseSpeciesImages);
    let workingImages: any = useRef(baseSpeciesImages);
    const speciesImageQueue = useRef<any[]>([]);
    const speciesImageQueueInterval = useRef<ReturnType<typeof setInterval>>();

    const clearSpeciesImageQueueInterval = useCallback(() => {
        if (!speciesImageQueueInterval.current) return;

        clearInterval(speciesImageQueueInterval.current);
        speciesImageQueueInterval.current = undefined;
    }, []);

    const processSpeciesImageQueue = useCallback(async () => {
        if (!speciesImageQueue.current.length) {
            clearSpeciesImageQueueInterval();
            setSpeciesImages(workingImages.current);
            return;
        }

        const nextQueueItem = speciesImageQueue.current.shift();
        // console.log('Next queue item is', nextQueueItem);

        try {
            // Remove subspecies and other modifiers like "(feral)" and "[cheriway group]" from the scientific name before trying to look up the image
            let speciesName = nextQueueItem.scientificName.replace(/[[(].+[\])]$/, '');
            const splitName = speciesName.split(' ');
            if (splitName.length > 2) {
                speciesName = `${splitName[0]} ${splitName[1]}`;
            }

            console.log(`Attempting to find species image for ${speciesName}`);

            const speciesImageQuery = `https://en.wikipedia.org/w/api.php?action=query&format=json&prop=pageimages&titles=${speciesName}&piprop=thumbnail%7Cname&pithumbsize=400&pilimit=50&redirects&origin=*`
            const response = await fetch(speciesImageQuery);
            const body = await response.json();
            const page: any = Object.values(body.query.pages)[0];

            const thumbUrl = page.thumbnail.source;
            const mediumUrl = page.thumbnail.source.replace(`${THUMB_SIZE}px`, `${MEDIUM_SIZE}px`);

            workingImages.current[nextQueueItem.taxonomicOrder] = {
                thumb: thumbUrl,
                medium: mediumUrl
            };

            console.log(`Assigning image ${thumbUrl} to taxon ID ${nextQueueItem.taxonomicOrder}`);
        } catch(_e) {
            console.log(`Unable to load image for ${nextQueueItem.scientificName}`);
        }
    }, [clearSpeciesImageQueueInterval]);

    const getSpeciesImages = useCallback((markers: any) => {
        clearSpeciesImageQueueInterval();
        workingImages.current = speciesImages ?? baseSpeciesImages;
        speciesImageQueue.current = [];

        for (let feature of markers?.features) {
            const props = feature.properties;
            if (!Object.prototype.hasOwnProperty.call(workingImages.current, props.taxonomicOrder)) {
                speciesImageQueue.current.push(props);
            }
        }

        if (!speciesImageQueue.current.length) {
            return;
        }

        void processSpeciesImageQueue();
        speciesImageQueueInterval.current = setInterval(processSpeciesImageQueue, 1000);
    }, [clearSpeciesImageQueueInterval, processSpeciesImageQueue, speciesImages]);

    useEffect(() => {
        return () => {
            clearSpeciesImageQueueInterval();
        }
    }, [clearSpeciesImageQueueInterval]);

    return {
        speciesImages,
        getSpeciesImages
    }
}
